"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";
import { ConvexError } from "convex/values";
import { captureError } from "@/lib/error-tracking";
import {
  isBrowserNetworkFailure,
  networkFailureCaptureContext,
} from "@/lib/telemetry/browser-network-failure";
import { recoverFromRetiredDeployment } from "@/lib/error-tracking/deployment-recovery";

type FallbackRender = (reset: () => void, error: Error | null) => ReactNode;

interface ErrorBoundaryProps {
  children: ReactNode;
  label: string;
  fallback?: ReactNode | FallbackRender;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
  shouldCapture?: boolean;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const isApplicationError = error instanceof ConvexError;
    const recovery = isApplicationError
      ? "not_chunk_load_error"
      : recoverFromRetiredDeployment(error);

    if (
      !isApplicationError &&
      recovery !== "reloading" &&
      this.props.shouldCapture !== false
    ) {
      captureError(error, {
        ...networkFailureCaptureContext(error, "react-error-boundary"),
        tags: {
          surface: "react-error-boundary",
          ...(isBrowserNetworkFailure(error) && { browser_network_failure: true }),
          boundaryLabel: this.props.label,
          ...(recovery === "exhausted" && {
            chunk_load_recovery_exhausted: true,
          }),
          ...(recovery === "storage_unavailable" && {
            chunk_load_recovery_storage_unavailable: true,
          }),
        },
        extra: {
          componentStack: info.componentStack,
        },
      });
    }

    this.props.onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  override render() {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      if (typeof fallback === "function") {
        return fallback(this.reset, error);
      }

      return fallback ?? null;
    }

    return this.props.children;
  }
}
